import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  choiceQuery,
  identifierQuery,
} from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationDateRange } from "components/scrapEyeDataDashboard/navigation/useNavigationDateRange";
import { useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TableCell,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTenantTranslation } from "hooks/formatters";
import {
  useListScrapEyeSegmentsByDevicesQuery,
  useListAvailableCameraAnglesQuery,
  type SegmentKind,
  SegmentRecord,
} from "src/store/api/generatedApi";
import { gcsUriToHttps } from "src/utils/gcs";
import dayjs from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";
import DateTimeControls from "./components/DateTimeControls";
import { List, RowComponentProps } from "react-window";

type Props = Readonly<{
  tenantName: string;
  deviceId: number;
}>;

type SegmentRowProps = RowComponentProps<{
  items: SegmentRecord[];
}>;

const SegmentRow = ({ index, style, items }: SegmentRowProps) => {
  const { t } = useTenantTranslation();
  const item = useMemo(() => items[index], [items, index]);
  if (!item) return null;
  return (
    <Box
      style={style}
      sx={{ display: "flex", borderBottom: 1, borderColor: "grey.100" }}
    >
      <TableCell sx={{ flex: "0 0 180px" }}>
        {new Date(item.segment_start).toLocaleString()}
      </TableCell>
      <TableCell sx={{ flex: "0 0 100px" }}>{item.kind}</TableCell>
      <TableCell sx={{ flex: "0 0 100px" }}>{item.status}</TableCell>
      <TableCell sx={{ flex: "0 0 200px" }}>
        {item.camera_angle
          ? `${item.camera_angle.location_name} - ${item.camera_angle.name}`
          : "-"}
      </TableCell>
      <TableCell sx={{ flex: "0 0 120px" }}>
        {item.segment_length_sec}
      </TableCell>
      <TableCell sx={{ flex: "0 0 120px" }}>{item.size_bytes ?? "-"}</TableCell>
      <TableCell sx={{ flex: "0 0 100px" }}>
        {item.mp4_path ? (
          <a
            href={gcsUriToHttps(item.mp4_path)}
            target="_blank"
            rel="noreferrer"
          >
            {t("openLink")}
          </a>
        ) : (
          "-"
        )}
      </TableCell>
    </Box>
  );
};

export default function SegmentsList({ tenantName, deviceId }: Props) {
  const [kind, setKind] = useNavigationQuery(
    "segmentKind",
    "",
    choiceQuery([
      "",
      "video",
      "metrics",
      "logs",
      "radio",
      "prediction",
      "embedding",
      "label",
    ] as const),
    "replace"
  );
  const [selectedAngleId, setSelectedAngleId] = useNavigationQuery(
    "segmentAngle",
    null,
    identifierQuery,
    "replace"
  );
  const { pickerValue: range, setPickerValue: setRange } =
    useNavigationDateRange("segmentRange", () => [
      dayjs.utc().subtract(1, "hour"),
      dayjs.utc(),
    ]);
  const startIso = range[0]?.toISOString();
  const endIso = range[1]?.toISOString();
  const { t } = useTenantTranslation();

  const { data, isLoading, isFetching, isError, refetch } =
    useListScrapEyeSegmentsByDevicesQuery(
      !startIso || !endIso
        ? (skipToken as never)
        : {
            tenantName,
            deviceIds: [deviceId],
            kind: kind || undefined,
            start: startIso,
            end: endIso,
          }
    );

  const allItems = useMemo(
    () => data?.items[deviceId] ?? [],
    [data?.items, deviceId]
  );

  const { data: cameraAnglesData } = useListAvailableCameraAnglesQuery({
    tenantName,
    deviceIds: [deviceId],
  });

  const items = useMemo(
    () =>
      selectedAngleId != null
        ? allItems.filter((item) => item.camera_angle?.id === selectedAngleId)
        : allItems,
    [allItems, selectedAngleId]
  );

  return (
    <Stack p={2} spacing={2}>
      <DateTimeControls
        pickerValue={range}
        onRangeChange={(val) => {
          setRange(val);
        }}
      />
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="seg-kind-label">{t("kindLabel")}</InputLabel>
          <Select
            labelId="seg-kind-label"
            value={kind}
            label={t("kindLabel")}
            onChange={(e: SelectChangeEvent) => {
              const v = e.target.value;
              setKind(v ? (v as SegmentKind) : "");
            }}
          >
            <MenuItem value="">{t("allLabel")}</MenuItem>
            <MenuItem value="video">{t("videoLabel")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="angle-label">{t("cameraAngle")}</InputLabel>
          <Select
            labelId="angle-label"
            value={selectedAngleId?.toString() ?? ""}
            label={t("cameraAngle")}
            onChange={(e: SelectChangeEvent) => {
              const value = e.target.value;
              setSelectedAngleId(value ? Number(value) : null);
            }}
          >
            <MenuItem value="">{t("allLabel")}</MenuItem>
            {cameraAnglesData?.items.map((angle) => (
              <MenuItem key={angle.id} value={angle.id.toString()}>
                {angle.location_name} - {angle.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={() => void refetch()} loading={isFetching}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack>
        {isLoading ? (
          <CircularProgress size={20} />
        ) : isError ? (
          <Alert severity="error">{t("errorLoadingSegments")}</Alert>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                borderBottom: 1,
                borderColor: "grey.100",
                bgcolor: "grey.50",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <TableCell
                sx={{ flex: "0 0 180px", fontWeight: "fontWeightBold" }}
              >
                {t("tableStart")}
              </TableCell>
              <TableCell
                sx={{ flex: "0 0 100px", fontWeight: "fontWeightBold" }}
              >
                {t("tableKind")}
              </TableCell>
              <TableCell
                sx={{ flex: "0 0 100px", fontWeight: "fontWeightBold" }}
              >
                {t("tableStatus")}
              </TableCell>
              <TableCell
                sx={{ flex: "0 0 200px", fontWeight: "fontWeightBold" }}
              >
                {t("cameraAngle")}
              </TableCell>
              <TableCell
                sx={{ flex: "0 0 120px", fontWeight: "fontWeightBold" }}
              >
                {t("tableLengthSeconds")}
              </TableCell>
              <TableCell
                sx={{ flex: "0 0 120px", fontWeight: "fontWeightBold" }}
              >
                {t("tableSizeBytes")}
              </TableCell>
              <TableCell
                sx={{ flex: "0 0 100px", fontWeight: "fontWeightBold" }}
              >
                GCS
              </TableCell>
            </Box>
            <List
              style={{ height: window.innerHeight - 450, width: "100%" }}
              rowCount={items.length}
              rowHeight={53}
              rowProps={{ items }}
              rowComponent={SegmentRow}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
